import { h, Fragment, Component, Element, State, Listen } from '@stencil/core';
import type { ComponentInterface } from '@stencil/core';
import { breakpoints } from 'utils';

const fullWidth = breakpoints.lg.up;

@Component({
	tag: 'app-menu',
	styleUrl: 'menu-shadow.scss',
	shadow: true
})
export class AppMenu implements ComponentInterface {
	@Element() root: HTMLElement;

	@State() fullWidth: boolean;
	@State() openMobile = false;
	@State() openMenuLevel1s = new Set<HTMLAppDetailsElement>();
	@State() forceOverlay = false;

	close() {
		this.openMobile = false;
		this.root.querySelectorAll('app-details').forEach(details => {
			details.open = false;
		});
		const olb = document.querySelector('app-menu > [slot=button] .olb');
		if(olb && olb.classList.contains('active')) {
			olb.classList.remove('active');
			this.forceOverlay = false;
		}
	}

	@Listen('olbToggle', {target: 'window'})
	handleOlbToggle(event: CustomEvent) {
		this.forceOverlay = event.detail.isActive;
		this.mobileLoginOpen();
	}
	
	@Listen('resize', {target: 'window'})
	handleResize() {
		this.mobileLoginOpen();
		this.setupReflectWidth;
	}

	mobileLoginOpen() {
		
		const olb = this.root.querySelector('app-menu > [slot=button] .olb');
		const collapsableMenu = this.root.shadowRoot?.querySelector('#collapsable-menu') as HTMLElement;

		if(olb && collapsableMenu) {
		  if(olb.classList.contains('active')) {
			  collapsableMenu.style.display = 'none';
		  } else {
			  collapsableMenu.style.display = 'block';
		  }
		}
	  }

	componentWillLoad() {
		const appDetailsElements = [...this.root.querySelectorAll('app-details')];
		const menuLevel1s = new Set(appDetailsElements.filter(details => details.name === 'menu-level-1'));

		this.root.addEventListener('appDetailsToggled', async (e: CustomEvent<boolean>) => {
			const details = e.composedPath()[0] as HTMLAppDetailsElement;
			if (!menuLevel1s.has(details))
				return;

			const openMenuLevel1s = new Set(this.openMenuLevel1s);

			if (details.open)
				openMenuLevel1s.add(details);
			else
				openMenuLevel1s.delete(details);

			// account for vdom orphans
			for (const details of openMenuLevel1s)
				if (!details.isConnected)
					openMenuLevel1s.delete(details);

			this.openMenuLevel1s = openMenuLevel1s;
		});
		// Initial setup, runs once when the component is first loaded
		this.setupReflectWidth();
	  }
	
	  componentDidUpdate() {
		// After each update (when `openMenuLevel1s` changes), rerun the reflectWidth logic
		this.setupReflectWidth();
	  }
	
	  setupReflectWidth() {
		const appDetailsElements = [...this.root.querySelectorAll('app-details')];
		const menuLevel1s = new Set(appDetailsElements.filter(details => details.name === 'menu-level-1'));
		const menuLevel2s = new Set(appDetailsElements.filter(details => details.name === 'menu-level-2'));
	
		const reflectWidth = () => {
		  this.fullWidth = fullWidth.matches;
	
		  menuLevel1s.forEach(details => {
			details.interestToggle = fullWidth.matches;
		  });
		  menuLevel2s.forEach(details => {
			details.forceShow = fullWidth.matches;
		  });
	
		  // For mobile, automatically open menu-level-2 when a menu-level-1 is open
		 
		  if (window.innerWidth < 991.98) {
			// Show related menu-level-2s for each open menu-level-1
			menuLevel1s.forEach(menuLevel1 => {
			  if (menuLevel1.open) {
				const menuTitle = document.querySelector('app-menu > [slot=menu] > div app-details[name=menu-level-1][open] > [slot=summary]')?.textContent?.trim();
				const multicolumnList = menuLevel1.querySelector('ul.multicolumn');
				const existingSpan = multicolumnList?.previousElementSibling;

				if(menuTitle && multicolumnList && (!existingSpan || existingSpan.tagName !== 'SPAN')) {
					const spanElement = document.createElement('span');
					spanElement.textContent = menuTitle;
					spanElement.classList.add('menu-title-span');

					multicolumnList.parentNode?.insertBefore(spanElement, multicolumnList);
				}
				// Show all associated menu-level-2s
				const menuLevel2sInside = multicolumnList?.querySelectorAll('li app-details[name="menu-level-2"]');
				menuLevel2sInside?.forEach(level2 => {
				  (level2 as HTMLAppDetailsElement).forceShow = true; // Show the menu-level-2
				});
			  }
			});
		  } else {
			document.querySelectorAll('.menu-title-span').forEach(span => span.remove());
		  }
		 
		};
		
		reflectWidth();
		fullWidth.addEventListener('change', reflectWidth);
		fullWidth.addEventListener('resize', reflectWidth);
	  }
	

	componentDidRender() {
		this.toggleSearch();
		this.copyRoutingnumber();
		this.setupReflectWidth();
	}

	toggleSearch() {
		const toggle = document.querySelector('.header-search-box-toggle') as HTMLElement;
		const form = document.querySelector('.header-search-box');
		const header = document.querySelector('#header') as HTMLElement;
		const olbActive = document.querySelector('app-menu > [slot=button] .olb.active');
		const closeToggle = document.querySelector('.header-search-box-close') as HTMLElement;
		const input = form?.querySelector('input');
	
		const openSearch = () => {
			toggle.classList.add('toggled');
			form?.classList.add('toggled');
			header.classList.add('search-open');
			olbActive?.classList.remove('active');
			this.forceOverlay = true;
	
			// Move focus to the search input
			setTimeout(() => input?.focus(), 100);
		};
	
		const closeSearch = () => {
			toggle.classList.remove('toggled');
			form?.classList.remove('toggled');
			header.classList.remove('search-open');
			this.forceOverlay = false;
	
			// Move focus back to the toggle button
			setTimeout(() => toggle?.focus(), 100);
		};
	
		if (toggle) {
			toggle.addEventListener('click', (e) => {
				e.preventDefault();
				openSearch();
			});

			toggle.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					openSearch();
				}
			});
		}
	
		if (closeToggle) {
			closeToggle.addEventListener('click', (e) => {
				e.preventDefault();
				closeSearch();
			});

			closeToggle.addEventListener('keydown', (e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					closeSearch();
				}
			})
		}
	
		// Handle Escape key to close search
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && form?.classList.contains('toggled')) {
				closeSearch();
			}
		});
	}
	
	closeHeaderSearch() {
		const toggle = document.querySelector('.header-search-box-toggle');
		const form = document.querySelector('.header-search-box');
		const header = document.querySelector('#header');
	
		if (toggle && form && header) {
			toggle.classList.remove('toggled');
			form.classList.remove('toggled');
			header.classList.remove('search-open');
		}
	
		this.forceOverlay = false;
	}

	copyRoutingnumber() {
		const copyBtn = document.getElementById('copy-btn')!;
		const routingNumber = document.getElementById('routing-number')!;
		const copyFeedback = document.getElementById('copy-feedback')!;

		copyBtn.addEventListener('click', () => {
			navigator.clipboard.writeText(routingNumber.textContent || '')
			.then(() => {
				copyFeedback.classList.remove('hidden');
				setTimeout(() => {
					copyFeedback.classList.add('hidden');
				}, 2000);
			})
			.catch(err => {
				console.error('Failed to copy text: ', err);
			});
		});
	};

	render() {

		const isOpen = (!this.fullWidth && this.openMobile) || this.openMenuLevel1s.size > 0;
		const body = document.querySelector('body');

		if(isOpen && body) {
			body.classList.add('menu-open');
		} else {
			if(!isOpen && body) {
				body.classList.remove('menu-open');
			}
		}

		return <>
			<app-overlay
				underNextSibling
				active={isOpen || this.forceOverlay}
				onOverlayDismissed={() => {
					this.close();
					this.closeHeaderSearch();
				}}
			/>
			<div id='container' part='container' class={isOpen ? 'open-nav' : ''}>
				{this.fullWidth
					?
						<>
							<div id='top-ribbon' part='top-ribbon'>
								<div class="container-fluid">
									<div>
										<slot name='routing-num' />
										<slot name='utilities' />
									</div>
									<slot name='search' />
								</div>
							</div>
							<div id='main-bar' part='main-bar'>
								<div class="container-fluid">
									<slot name='logo' />
									<div class="nav-elements">
										<slot name='menu' />
										<slot name='button' />
									</div>
								</div>
							</div>
						</>
					:
						<>
							<slot name='button' />
							<slot name='logo' />
							
							<app-details
								id='collapsable-menu'
								part='collapsable-menu'
								exportparts='content: collapsable-menu-content'
								class="mobile-menu"
								open={isOpen}
								cssHide
								onAppDetailsToggled={e => {
									const kdxDetails = e.composedPath()[0] as HTMLAppDetailsElement;
									if (kdxDetails !== e.currentTarget)
										return;

									if (e.detail) {
										this.openMobile = true;
									}
									else {
										this.close();
									}
								}}
							>
								<div slot="open-icon">
									<app-icon icon='menu' />
									<span>Menu</span>
								</div>

								<div class="close-opened-menu" onClick={() => {this.close()}}>
									<div>
										<app-icon icon='close' />
									</div>
								</div>
								<div class="mobile-search-toggle" role="button" tabIndex={0}>
									
									<slot name='search' />
								</div>

								<div class="mobile-search-box">
									<slot name='search' />
								</div>
								<slot name='menu' />
								<slot name='utilities' />
								<slot name='routing-num' />
							</app-details>
						</>
				}
				<slot />
			</div>
		</>;
	}
}
